import { motion } from 'framer-motion';
import { 
  CheckCircle, Circle, ChevronRight, Users, 
  FileText, Megaphone, Rocket, Heart
} from 'lucide-react';

// Change Management Lifecycle - focused on the PEOPLE side
const JOURNEY_STAGES = [
  {
    id: 'assess',
    name: 'Assess',
    description: "Who's affected & how",
    icon: Users,
    tasks: [
      { id: 'who_affected', label: "Identify who's affected" },
      { id: 'impact_size', label: 'Understand the impact' },
      { id: 'resistance', label: 'Spot likely resistance' },
      { id: 'readiness', label: 'Check change readiness' },
    ]
  },
  {
    id: 'plan',
    name: 'Plan',
    description: 'How to bring people along',
    icon: FileText,
    tasks: [
      { id: 'comms_plan', label: 'Plan communications' },
      { id: 'training_plan', label: 'Plan training & support' },
      { id: 'stakeholder_plan', label: 'Stakeholder strategy' },
      { id: 'resistance_plan', label: 'Plan for resistance' },
    ]
  },
  {
    id: 'build',
    name: 'Build',
    description: 'Create your change materials',
    icon: Megaphone,
    tasks: [
      { id: 'comms_created', label: 'Create communications' },
      { id: 'training_ready', label: 'Build training materials' },
      { id: 'managers_prepped', label: 'Prepare managers' },
      { id: 'sponsors_briefed', label: 'Brief sponsors' },
    ]
  },
  {
    id: 'implement',
    name: 'Implement',
    description: 'Support the transition',
    icon: Rocket,
    tasks: [
      { id: 'go_live_support', label: 'Provide go-live support' },
      { id: 'issues_handled', label: 'Address issues quickly' },
      { id: 'track_adoption', label: 'Track adoption' },
    ]
  },
  {
    id: 'embed',
    name: 'Embed',
    description: 'Make it stick',
    icon: Heart,
    tasks: [
      { id: 'reinforce', label: 'Reinforce new behaviours' },
      { id: 'stragglers', label: 'Support non-adopters' },
      { id: 'measure', label: 'Measure success' },
    ]
  }
];

export default function JourneyProgress({ 
  completedTasks = [], 
  currentStage = 'assess',
  compact = false,
  onStageClick 
}) {
  const getStageStatus = (stage) => {
    const completedInStage = stage.tasks.filter(task => 
      completedTasks.includes(task.id)
    ).length;
    
    if (completedInStage === stage.tasks.length) return 'complete';
    if (completedInStage > 0) return 'in-progress';
    return 'pending';
  };

  const currentStageIndex = JOURNEY_STAGES.findIndex(s => s.id === currentStage);

  if (compact) {
    return (
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <p className="text-xs text-slate-500 font-medium">CHANGE JOURNEY</p>
          <div className="flex items-center gap-1">
            {JOURNEY_STAGES.map((stage, index) => {
              const status = getStageStatus(stage);
              const isCurrent = stage.id === currentStage;
              const Icon = stage.icon;
              
              return (
                <div key={stage.id} className="flex items-center">
                  <button
                    onClick={() => onStageClick?.(stage.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:scale-105 ${
                      status === 'complete' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : isCurrent 
                          ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' 
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    title={`${stage.name}: ${stage.description}`}
                    data-testid={`stage-${stage.id}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{stage.name}</span>
                  </button>
                  {index < JOURNEY_STAGES.length - 1 && (
                    <ChevronRight className="h-3 w-3 text-slate-300 mx-0.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Full view for sidebar or dedicated section
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Your Change Journey</h3>
      
      <div className="space-y-3">
        {JOURNEY_STAGES.map((stage, index) => {
          const status = getStageStatus(stage);
          const isCurrent = stage.id === currentStage;
          const Icon = stage.icon;
          
          return (
            <motion.button
              key={stage.id}
              onClick={() => onStageClick?.(stage.id)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all ${
                isCurrent 
                  ? 'bg-amber-50 border border-amber-200' 
                  : status === 'complete'
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
              }`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid={`stage-full-${stage.id}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                status === 'complete' 
                  ? 'bg-emerald-500' 
                  : isCurrent 
                    ? 'bg-amber-500' 
                    : 'bg-slate-300'
              }`}>
                {status === 'complete' ? (
                  <CheckCircle className="h-4 w-4 text-white" />
                ) : (
                  <Icon className="h-4 w-4 text-white" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className={`font-medium ${
                    isCurrent ? 'text-amber-900' : status === 'complete' ? 'text-emerald-900' : 'text-slate-700'
                  }`}>
                    {stage.name}
                  </h4>
                  {status === 'in-progress' && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      In progress
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-0.5 ${
                  isCurrent ? 'text-amber-700' : status === 'complete' ? 'text-emerald-700' : 'text-slate-500'
                }`}>
                  {stage.description}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
      
      <div className="mt-4 pt-4 border-t border-slate-200">
        <p className="text-xs text-slate-500 text-center">
          Click any stage to see what to do and what you've captured
        </p>
      </div>
    </div>
  );
}

export { JOURNEY_STAGES };
